/**
 * PATH: packages/ui/src/contexts/HubContext.tsx
 * PURPOSE: Provides hub identity context so components know which hub they
 *          belong to and can compute correct navigation paths.
 *
 * WHY: When hubs are mounted under a prefix (e.g., /workforce/*), components
 *      need to resolve absolute paths relative to their hub root. This context
 *      provides the basePath and hubId so useHubNavigate can prepend the prefix.
 *
 * USAGE:
 *   // In shell:
 *   <HubProvider hubId="workforce" basePath="/workforce">
 *     <WorkforceRoutes />
 *   </HubProvider>
 *
 *   // In hub components:
 *   const { hubId, basePath } = useHub()
 *   const navigate = useHubNavigate()
 *   navigate('/people')  // → navigates to /workforce/people
 */

import { createContext, useContext, useCallback, type ReactNode } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'

// ─── Context ───────────────────────────────────────────────

interface HubContextValue {
  /** Hub identifier (e.g., 'workforce', 'crm') */
  hubId: string
  /** Base path prefix for this hub (e.g., '/workforce') */
  basePath: string
  /** Whether running in unified shell (true) or standalone mode (false) */
  isUnified: boolean
}

const HubContext = createContext<HubContextValue>({
  hubId: '',
  basePath: '',
  isUnified: false,
})

// ─── Provider ──────────────────────────────────────────────

interface HubProviderProps {
  hubId: string
  basePath: string
  children: ReactNode
}

export function HubProvider({ hubId, basePath, children }: HubProviderProps) {
  return (
    <HubContext.Provider value={{ hubId, basePath, isUnified: basePath !== '' }}>
      {children}
    </HubContext.Provider>
  )
}

// ─── Hooks ─────────────────────────────────────────────────

/** Get the current hub context */
export function useHub() {
  return useContext(HubContext)
}

/**
 * Navigate with hub-awareness. Absolute paths (starting with /)
 * are automatically prefixed with the hub's basePath when running
 * in unified mode.
 *
 * @example
 *   const navigate = useHubNavigate()
 *   navigate('/people')         // → /workforce/people (in unified mode)
 *   navigate('/people')         // → /people (in standalone mode)
 *   navigate('details')         // → relative (unchanged in both modes)
 *   navigate(-1)                // → back (unchanged)
 */
export function useHubNavigate() {
  const navigate = useNavigate()
  const { basePath } = useContext(HubContext)

  return useCallback(
    (to: string | number, options?: { replace?: boolean; state?: any }) => {
      if (typeof to === 'number') {
        return navigate(to)
      }
      // Prefix absolute paths with hub basePath
      if (to.startsWith('/') && basePath) {
        return navigate(`${basePath}${to}`, options)
      }
      return navigate(to, options)
    },
    [navigate, basePath]
  )
}

/**
 * Get the hub-relative location pathname.
 * Strips the hub prefix so hub components see paths like '/people'
 * instead of '/workforce/people'.
 */
export function useHubLocation() {
  const location = useLocation()
  const { basePath } = useContext(HubContext)

  const hubPathname = basePath && location.pathname.startsWith(basePath)
    ? location.pathname.slice(basePath.length) || '/'
    : location.pathname

  return {
    ...location,
    pathname: hubPathname,
    /** The full pathname including hub prefix */
    fullPathname: location.pathname,
  }
}
